import { Box, DialogActions, DialogContent } from '@mui/material';
// import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import styled from '@mui/material/styles/styled';
import Typography from '@mui/material/Typography';
import { differenceInHours, differenceInWeeks, isBefore } from 'date-fns';
import { forEach, isEmpty, size } from 'lodash-es';
import PropTypes from 'prop-types';
import React from 'react';
import { MdArrowBackIos, MdBackHand, MdClose } from 'react-icons/md';
import { VolunteerStatus } from '../../constants/volunteering.constant';
import { initiateSendPost } from '../../stores/postActions';
import useStore from '../../stores/store';
import { formatDisplayDate, formatDisplayTime } from '../../utils/dateHelpers';
import getQualifiedImageUrl from '../../utils/imageUrlProcessor';
import NonProfitBar from '../NonProfitBar/NonProfitBar';
import TopPostContributingUsers from '../SocialFeed/TopPostContributingUsers';
import StyledAnchor from '../Styled/StyledAnchor';
import StyledBoldTypography from '../Styled/StyledBoldTypography';
import StyledContainedButton from '../Styled/StyledContainedButton';
import StyledRoundIconButton from '../Styled/styledRoundIconButton';
import StyledTextButton from '../Styled/StyledTextButton';
import VolunteeringOpportunityModel from './volunteeringOpportunityModel';
import { formatNumberForLocale } from '../../utils/stringHelpers';
import ClickableTooltip from '../Styled/ClickableTooltip';
import PostImages from '@components/PostImages';
import PostVideos from '@components/PostVideos';
import PostMessage from '../next-page-components/PostMessage';
import Chip from '@ui/Chip';

const StyledNonProfitBarWrapper = styled('div')(() => ({
  display: 'flex',
  height: 60,
}));

const StyledImage = styled('img')(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: 16,
}));

const StyledVolunteerProgress = styled(LinearProgress)(() => ({
  height: 10,
  borderRadius: 5,
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
  },
}));

const webLinkRegEx =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;

function VolunteeringDetails(props) {
  const {
    opportunity = null,
    userVolunteeringStatus = 'none',
    // volunteers,
    onSubmit,
    onLogEffort,
    nonProfit,
  } = props;

  const userProfile = useStore((state) => state.user.profile);

  const hasEnded = isEmpty(opportunity.applyDeadline)
    ? isBefore(new Date(opportunity.endsOn), new Date())
    : isBefore(new Date(opportunity.applyDeadline), new Date());

  const getScheduleText = () => {
    if (opportunity.type === 'flexible') {
      return 'Flexible';
    }
    return `${formatDisplayTime(opportunity.startsAt)} - ${formatDisplayTime(
      opportunity.endsAt,
    )}`;
  };

  const getLocationData = () => {
    const locationData = [];
    if (opportunity.virtual) {
      const parts = opportunity.location.split(webLinkRegEx);
      forEach(parts, (part, index) => {
        if (!isEmpty(part)) {
          if (webLinkRegEx.test(part)) {
            locationData.push({
              id: index,
              type: 'link',
              value: part,
            });
          } else {
            locationData.push({
              id: index,
              type: 'text',
              value: part,
            });
          }
        }
      });
    } else {
      locationData.push({
        id: 0,
        type: 'text',
        value: opportunity.location,
      });
    }

    return locationData;
  };

  const getTimeCommitmentText = () => {
    // if (opportunity.type === 'flexible') {
    //   return `${opportunity.timeCommitmentMin} - ${
    //     opportunity.timeCommitmentMax
    //   } hours over ${differenceInWeeks(
    //     new Date(opportunity.endsOn),
    //     new Date(opportunity.startsOn),
    //   )} weeks`;
    // }
    if (opportunity.type === 'flexible') {
      return `${opportunity.timeCommitmentMin} hours over ${differenceInWeeks(
        new Date(opportunity.endsOn),
        new Date(opportunity.startsOn),
      )} weeks`;
    }
    return `${differenceInHours(
      new Date(opportunity.endsAt),
      new Date(opportunity.startsAt),
    )} hours`;
  };

  const handleSendPost = () => {
    initiateSendPost(opportunity.postId);
  };

  if (opportunity) {
    return (
      <>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {/* ----------- Description ----------------*/}
              <Stack spacing={2}>
                {/* // TODO: check */}
                {/* <Typography variant="body2">
                  {opportunity.description}
                </Typography> */}
                <div className="py-2">
                  <PostMessage message={opportunity.description} />
                </div>

                {/* ----------- Non Profit Bar ----------------*/}
                <StyledNonProfitBarWrapper>
                  <NonProfitBar
                    npo={nonProfit}
                    nonProfitId={
                      opportunity?.nonProfitId || opportunity?.nonProfit?.id
                    }
                    userVolunteeringStatus={userVolunteeringStatus}
                    onLogEffort={onLogEffort}
                    onSubmit={onSubmit}
                    opportunity={opportunity}
                  />
                </StyledNonProfitBarWrapper>

                <Stack>
                  {(() => {
                    if (
                      size(opportunity.imageUrls) > 0 &&
                      size(opportunity.videoUrls) > 0
                    ) {
                      return (
                        <div className="relative flex aspect-video w-full items-center justify-center">
                          <PostImages
                            imageUrls={opportunity.imageUrls}
                            bgColor={'bg-image-background'}
                          />
                          <PostVideos videoUrls={opportunity.videoUrls || []} />
                          <div className="absolute right-1 top-1">
                            {/* <Chip
                              label={
                                opportunity.virtual ? 'Virtual' : 'In-person'
                              }
                              variant="outline"
                              color="secondary"
                            /> */}
                            <Chip color="secondary" text="small">
                              {opportunity.virtual ? 'Virtual' : 'In-person'}
                            </Chip>
                          </div>
                        </div>
                      );
                    } else if (size(opportunity.imageUrls) > 0) {
                      return (
                        <div className="relative flex aspect-video w-full items-center justify-center">
                          <PostImages
                            imageUrls={opportunity.imageUrls}
                            bgColor={'bg-image-background'}
                          />
                          <div className="absolute right-1 top-1">
                            {/* <Chip
                              label={
                                opportunity.virtual ? 'Virtual' : 'In-person'
                              }
                              variant="outline"
                              color="secondary"
                            /> */}
                            <Chip color="secondary" text="small">
                              {opportunity.virtual ? 'Virtual' : 'In-person'}
                            </Chip>
                          </div>
                        </div>
                      );
                    } else if (size(opportunity.videoUrls) > 0) {
                      return (
                        <div className="relative flex aspect-video w-full items-center justify-center">
                          <PostVideos videoUrls={opportunity.videoUrls || []} />
                          <div className="absolute right-1 top-1">
                            {/* <Chip
                              label={
                                opportunity.virtual ? 'Virtual' : 'In-person'
                              }
                              variant="outline"
                              color="secondary"
                            /> */}
                            <Chip color="secondary" text="small">
                              {opportunity.virtual ? 'Virtual' : 'In-person'}
                            </Chip>
                          </div>
                        </div>
                      );
                    }
                  })()}
                </Stack>

                {/* ----------- Skills ----------------*/}
                <Stack
                  sx={{
                    flexDirection: { sm: 'column', md: 'row' },
                  }}
                  gap={3}>
                  <Stack spacing={1} flex="1">
                    <StyledBoldTypography variant="body2">
                      Mandatory Skills
                    </StyledBoldTypography>
                    <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
                      {opportunity?.mandatorySkills?.map((skill) => (
                        <Chip key={skill.id} color="secondary" text="small">
                          {skill.skill_title}
                        </Chip>
                      ))}
                    </Stack>
                  </Stack>

                  <Stack spacing={1} flex="1">
                    <StyledBoldTypography variant="body2">
                      Good to have Skills
                    </StyledBoldTypography>
                    <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
                      {opportunity?.optionalSkills?.map((skill) => (
                        <Chip key={skill.id} color="secondary" text="small">
                          {skill.skill_title}
                        </Chip>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>

                {/* ----------- Time and Location ----------------*/}
                <Stack spacing={1}>
                  <Stack
                    sx={{
                      flexDirection: { sm: 'column', md: 'row' },
                      alignItems: { sm: 'start', md: 'center' },
                    }}
                    gap={3}>
                    <Stack direction="row" alignItems="center" flex="1" gap={1}>
                      <StyledBoldTypography variant="body2">
                        When:
                      </StyledBoldTypography>
                      <StyledBoldTypography variant="body1">
                        {formatDisplayDate(opportunity.startsOn)}
                        {opportunity.type === 'flexible' &&
                          ` - ${formatDisplayDate(opportunity.endsOn)}`}
                      </StyledBoldTypography>
                    </Stack>
                    <Stack direction="row" alignItems="center" flex="1" gap={1}>
                      <StyledBoldTypography variant="body2">
                        Time:
                      </StyledBoldTypography>
                      <StyledBoldTypography variant="body1">
                        {getScheduleText()}
                      </StyledBoldTypography>
                    </Stack>
                  </Stack>
                  {opportunity.location && (
                    <Stack direction="row" flexWrap="wrap">
                      {getLocationData().map((data) => {
                        if (data.type === 'text') {
                          return (
                            <Typography
                              key={data.id}
                              variant="body1"
                              sx={{ mr: 1 }}>
                              {data.value}
                            </Typography>
                          );
                        }
                        return (
                          <StyledAnchor
                            key={data.id}
                            component="a"
                            href={data.value}
                            target="_blank"
                            rel="noopener noreferrer">
                            {data.value}
                          </StyledAnchor>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>

                {/* ----------- Savings and Time Commitment ----------------*/}
                <Stack
                  sx={{
                    flexDirection: { sm: 'column', md: 'row' },
                    alignItems: { sm: 'start', md: 'center' },
                  }}
                  gap={3}>
                  {/* // TODO: check */}
                  {nonProfit?.stripe_id ? (
                    <Stack flex="1" gap={1}>
                      <StyledBoldTypography variant="body2">
                        Time Commitment:
                      </StyledBoldTypography>
                      <StyledBoldTypography variant="body1">
                        {getTimeCommitmentText()}
                      </StyledBoldTypography>
                    </Stack>
                  ) : (
                    <>
                      {/* <Stack flex="1" gap={1}>
                        <StyledBoldTypography variant="body2">
                          Estimated Savings:
                        </StyledBoldTypography>
                        <StyledBoldTypography variant="body1">
                          {`Saves ${formatNumberForLocale(
                            opportunity.timeCommitmentMax,
                          )} of work at ${
                            userProfile.location.currencySymbol
                          }${formatNumberForLocale(
                            opportunity.estimatedBillRate,
                          )} hour rate = ${
                            userProfile.location.currencySymbol
                          }${formatNumberForLocale(
                            opportunity.timeCommitmentMax *
                              opportunity.estimatedBillRate,
                          )}`}
                        </StyledBoldTypography>
                      </Stack> */}
                      <Stack flex="1" gap={1}>
                        <StyledBoldTypography variant="body2">
                          Time Commitment:
                        </StyledBoldTypography>
                        <StyledBoldTypography variant="body1">
                          {getTimeCommitmentText()}
                        </StyledBoldTypography>
                      </Stack>
                    </>
                  )}
                </Stack>
                {/* ----------- Volunteer requirements ----------------*/}
                <Stack gap={2}>
                  <Stack gap={1}>
                    <StyledBoldTypography variant="body2">
                      Volunteers Required:
                    </StyledBoldTypography>

                    <div style={{ position: 'relative' }}>
                      <StyledVolunteerProgress
                        value={Math.floor(
                          (opportunity.totalVolunteers /
                            opportunity.volunteersReq) *
                            100,
                        )}
                        variant="determinate"
                        color="secondary"
                        sx={{ height: 10, borderRadius: 8 }}
                      />

                      <div
                        style={{
                          position: 'absolute',
                          left: `calc(${
                            (opportunity.totalVolunteers /
                              opportunity.volunteersReq) *
                            100
                          }% - 10px)`,
                          top: '-5px',
                        }}>
                        <MdBackHand />
                        <StyledBoldTypography
                          variant="caption"
                          sx={{ transform: 'translateX(-50%)' }}>
                          {opportunity.volunteersReq -
                            opportunity.totalVolunteers}{' '}
                          / {opportunity.volunteersReq}
                        </StyledBoldTypography>
                      </div>
                    </div>
                  </Stack>
                  <Stack
                    sx={{
                      flexDirection: { sm: 'column', md: 'row' },
                      alignItems: { sm: 'start', md: 'center' },
                    }}
                    gap={3}>
                    <Stack flex="1" mt={2}>
                      {!isEmpty(opportunity?.additionalDetails) && (
                        <StyledBoldTypography variant="body2">
                          {opportunity.additionalDetails}
                        </StyledBoldTypography>
                      )}
                    </Stack>
                    {/* // TODO: check - logic for sending post */}
                    {/* <Stack
                      direction="row"
                      flex="1"
                      alignItems="end"
                      justifyContent="end"
                      alignSelf="end"
                      spacing={2}
                      mt={2}>
                      <StyledTextButton type="button" onClick={handleSendPost}>
                        Send
                      </StyledTextButton>
                    </Stack> */}
                  </Stack>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        {/* <Divider className="my-1" variant="middle" /> */}
        <DialogActions>
          <Stack
            gap={1}
            direction="row"
            justifyContent="start"
            alignItems="center"
            pl={2}
            width="100%">
            <StyledBoldTypography variant="body2" color="primary">
              Look who else is volunteering for this
            </StyledBoldTypography>
            <ClickableTooltip
              message={
                'People from other organizations may be volunteering for this opportunity'
              }
            />
            <TopPostContributingUsers users={opportunity?.companyVolunteers} />
          </Stack>
        </DialogActions>
      </>
    );
  }
  return null;
}

export default VolunteeringDetails;
